import { NgFor, NgIf, NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';
import { PushPipe } from '@ngrx/component';
import { Subject } from 'rxjs';
import { debounceTime, startWith, takeUntil } from 'rxjs/operators';

import { trackByProp } from '@recruitee/common';
import { User } from '@recruitee/http/types';
import { TranslatePipe } from '@recruitee/i18n';
import {
  AdminFilterPipe,
  AvatarComponent,
  BoxComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  CheckboxComponent,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DividerComponent,
  EmptyStateComponent,
  FormFieldComponent,
  GroupComponent,
  HeadingComponent,
  IconColorDirective,
  IconComponent,
  InputComponent,
  RtTooltip,
  SpinnerComponent,
  TruncateComponent,
} from '@recruitee/user-interface';

// TODO translations got wrong namespace app, probably should be moved into rtu namespace, so full component could be reused

@Component({
  selector: 'rt-admins-select-dialog-form',
  templateUrl: './admins-select-dialog-form.component.html',
  styleUrls: ['./admins-select-dialog-form.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AvatarComponent,
    BoxComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    CheckboxComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    DividerComponent,
    EmptyStateComponent,
    FormFieldComponent,
    GroupComponent,
    HeadingComponent,
    IconColorDirective,
    IconComponent,
    InputComponent,
    NgFor,
    NgIf,
    NgTemplateOutlet,
    ReactiveFormsModule,
    RtTooltip,
    SpinnerComponent,
    TranslatePipe,
    VirtualScrollerModule,
    TruncateComponent,
    UpperCasePipe,
    PushPipe,
  ],
  providers: [AdminFilterPipe],
})
export class AdminsSelectDialogFormComponent {
  private static VIRTUAL_SCROLL_THRESHOLD = 20;

  @Input() public heading: string;
  @Input() public subheading: string;
  @Input() public assignments: number[] = [];
  @Input() public admins: User[] = [];
  @Input() public disallowedUsers: User[] = [];
  @Input() public currentAdminId: number | null;
  @Input() public loading: boolean = false;
  @Input() public loadingAdmins: boolean = false;
  @Input() public preventAdminRemove: boolean = false;
  @Input() public showCountBeforeSubheading: boolean = false;
  @Input() public minSelectedAdmins: number = 0;
  @Input() public maxSelectedAdmins: number = 0;
  @Input() public lockedAssignments: number[] = [];
  @Input() public lockedAssignmentsTooltip: string | null = null;
  @Input() public isATCEnabled = false;
  @Input() public accessToCandidateEnabled = false;
  @Input() public inviteModeEnabled = false;
  @Input() public shareOnTheGoEnabled = false;

  @Output() public cancelClick: EventEmitter<any> = new EventEmitter();
  @Output() public submitClick: EventEmitter<any> = new EventEmitter();

  public disabledIds: Record<number, boolean> = {};
  public selectedIds = new Set<number>();
  public query = '';
  public searchControl = new FormControl<string>('', { nonNullable: true });
  public trackById = trackByProp<{ id: number }>({ prop: 'id' });
  public virtualScrollUsers: User[] = [];
  public virtualScrollDisabledUsers: User[] = [];
  public virtualScrollItems: User[] = [];

  public filteredUsers: User[] = this.admins;
  public filteredDisallowedUsers: User[] = this.disallowedUsers;

  public get filteredUsersExceedVirtualScrollThreshold(): boolean {
    return (
      this.filteredUsers.length + this.filteredDisallowedUsers.length >
      AdminsSelectDialogFormComponent.VIRTUAL_SCROLL_THRESHOLD
    );
  }

  private ngOnDestroy$ = new Subject<void>();

  constructor(protected cdRef: ChangeDetectorRef, private adminFilterPipe: AdminFilterPipe) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.assignments &&
      changes.assignments.currentValue &&
      changes.assignments.firstChange
    ) {
      this.selectedIds = new Set(this.assignments);
      this.disabledIds = this.admins.reduce(
        (result, admin) => ({
          ...result,
          [admin.id]:
            this.lockedAssignments.includes(admin.id) ||
            (this.preventAdminRemove && this.currentAdminId === admin.id),
        }),
        {},
      );
    }
  }

  public ngOnInit(): void {
    const destroy$ = this.ngOnDestroy$.asObservable();
    this.searchControl.valueChanges
      .pipe(debounceTime(100), startWith(''), takeUntil(destroy$))
      .subscribe((searchControlValue: string) => {
        this.query = searchControlValue;
        this.filteredUsers = this.adminFilterPipe.transform(this.admins, searchControlValue);

        this.filteredDisallowedUsers = this.isATCEnabled
          ? this.adminFilterPipe.transform(this.disallowedUsers, searchControlValue)
          : [];

        this.virtualScrollItems = this.filteredUsers
          .concat(this.filteredDisallowedUsers)
          .sort((a, _) => (a.hasCandidateAccess ? -1 : 1));
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public selectAll(): void {
    this.admins.forEach(admin => {
      this.selectedIds.add(admin.id);
      this.cdRef.detectChanges();
    });
  }

  public selectNone(): void {
    this.admins.forEach(admin => {
      if (!this.disabledIds[admin.id]) this.selectedIds.delete(admin.id);
    });
    this.cdRef.detectChanges();
  }

  public toggleAssignment(admin: User): void {
    if (this.disabledIds[admin.id]) return;

    this.selectedIds.has(admin.id)
      ? this.selectedIds.delete(admin.id)
      : this.selectedIds.add(admin.id);
    this.cdRef.detectChanges();
  }

  public getSelectedCount(): number {
    return this.selectedIds.size;
  }

  public cancelSearch(): void {
    this.searchControl.reset('');
  }

  public cancel(): void {
    this.cancelClick.emit();
  }

  public isValid(): boolean {
    if (this.preventAdminRemove) {
      const selectedUsers = this.admins.filter(admin => this.selectedIds.has(admin.id));
      if (!selectedUsers.map(admin => admin.id).includes(<number>this.currentAdminId)) {
        return false;
      }
    }
    if (this.minSelectedAdmins && this.getSelectedCount() < this.minSelectedAdmins) {
      return false;
    }
    if (this.maxSelectedAdmins && this.getSelectedCount() > this.maxSelectedAdmins) {
      return false;
    }

    return true;
  }

  public submit(): void {
    const selectedUsers = this.admins.filter(admin => this.selectedIds.has(admin.id));
    this.submitClick.emit(selectedUsers);
  }

  public onVirtualScrollUpdate(event: User[]) {
    this.virtualScrollUsers = event.filter(
      admin => admin.hasCandidateAccess === true || admin.hasCandidateAccess === undefined,
    );

    this.virtualScrollDisabledUsers = this.isATCEnabled
      ? event.filter(admin => admin.hasCandidateAccess === false)
      : [];

    this.cdRef.detectChanges();
  }
}
